import { Add } from "@mui/icons-material";
import {
    Autocomplete,
    Button,
    CircularProgress,
    Dialog,
    Grid,
    IconButton,
    TextField,
    Tooltip,
} from "@mui/material";
import { createFilterOptions } from "@mui/material/Autocomplete";
import { useIsMutating } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import { useContext, useEffect, useState } from "react";
import { PosContext } from "../../../../shared/PosContext";
import { useCreateDoctypeData } from "../../../../shared/hooks/useDocTypesData";
import { useStyles } from "./styles";

const filter = createFilterOptions();
const DEFAULT_TERRITORY = "All Territories";

const NewAddress = ({ customerName, onClose, onCreate }) => {
    const classes = useStyles();
    const isMutating = useIsMutating();
    const { territoryOptions, refetchAddresses } = useContext(PosContext);

    const [loading, setLoading] = useState(false);

    const createAddressMutation = useCreateDoctypeData();
    const createTerritoryMutation = useCreateDoctypeData();

    const [localTerritories, setLocalTerritories] = useState([DEFAULT_TERRITORY]);
    const [territoryInputValue, setTerritoryInputValue] = useState("");
    const [newTerritoryDialogOpen, setNewTerritoryDialogOpen] = useState(false);
    const [newTerritoryName, setNewTerritoryName] = useState("");

    useEffect(() => {
        if (isMutating) {
            setLoading(true);
        } else {
            setLoading(false);
        }
    }, [isMutating]);

    const [formData, setFormData] = useState({
        address_line1: "",
        address_line2: "",
        territory: DEFAULT_TERRITORY,
        phone: "",
    });

    const requiredFields = ["territory", "address_line1"];
    const formDefaults = {
        docstatus: 1,
        doctype: "Address",
        name: "new-address-1",
        address_type: "Billing",
        city: "رامالله",
        links: [
            {
                docstatus: 1,
                doctype: "Dynamic+Link",
                name: "new-dynamic-link-1",
                parent: "new-address-1",
                parentfield: "links",
                parenttype: "Address",
                idx: 1,
                link_doctype: "Customer",
                link_name: customerName,
            },
        ],
    };

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleReset = () => {
        setFormData({
            address_line1: "",
            address_line2: "",
            territory: DEFAULT_TERRITORY,
            phone: "",
        });
        setTerritoryInputValue("");
    };

    const handleSubmit = () => {
        let requiredField = null;

        requiredFields.forEach((field) => {
            if (!requiredField && formData[field] === "") {
                requiredField = field;
                return;
            }
        });

        if (requiredField) {
            enqueueSnackbar(`${requiredField} is required !`);
            return;
        }

        createAddressMutation.mutate(
            {
                doctype: "Address",
                formData: { ...formDefaults, ...formData },
            },
            {
                onSuccess: (data) => {
                    refetchAddresses();
                    onCreate?.(data?.data);
                },
            }
        );
    };

    useEffect(() => {
        const base = Array.isArray(territoryOptions)
            ? territoryOptions.filter(Boolean)
            : [];
        const withDefault = base.includes(DEFAULT_TERRITORY)
            ? base
            : [DEFAULT_TERRITORY, ...base];
        setLocalTerritories(withDefault);
    }, [territoryOptions]);

    const createTerritory = async (territoryName) => {
        const name = String(territoryName || "").trim();
        if (!name) return;
        if (localTerritories.includes(name)) {
            setFormData((prev) => ({ ...prev, territory: name }));
            return;
        }

        createTerritoryMutation.mutate(
            {
                doctype: "Territory",
                formData: {
                    doctype: "Territory",
                    territory_name: name,
                    // Keep it under the standard root if it exists.
                    parent_territory: DEFAULT_TERRITORY,
                    is_group: 0,
                },
            },
            {
                onSuccess: () => {
                    setLocalTerritories((prev) =>
                        prev.includes(name) ? prev : [...prev, name]
                    );
                    setFormData((prev) => ({ ...prev, territory: name }));
                    setTerritoryInputValue(name);
                },
                onError: (err) => {
                    const msg =
                        err?.response?.data?.exception ||
                        err?.response?.data?.message ||
                        "Failed to create territory";
                    enqueueSnackbar(msg);
                },
            }
        );
    };

    return (
        <div className={classes.NewAddressWrapper}>
            <Grid container spacing={3}>
                <Grid item xs={12} sm={12}>
                    <Grid container spacing={1} alignItems="center">
                        <Grid item xs={10}>
                            <Autocomplete
                                value={formData.territory}
                                inputValue={territoryInputValue}
                                onInputChange={(_, v) => setTerritoryInputValue(v)}
                                onChange={(e, val) => {
                                    if (typeof val === "string") {
                                        setFormData({
                                            ...formData,
                                            ["territory"]: val || DEFAULT_TERRITORY,
                                        });
                                        return;
                                    }
                                    if (val && val.inputValue) {
                                        createTerritory(val.inputValue);
                                        return;
                                    }
                                    setFormData({
                                        ...formData,
                                        ["territory"]: val || DEFAULT_TERRITORY,
                                    });
                                }}
                                options={localTerritories}
                                getOptionLabel={(option) =>
                                    typeof option === "string" ? option : option.title
                                }
                                freeSolo
                                filterOptions={(options, params) => {
                                    const filtered = filter(options, params);
                                    const inputValue = params.inputValue?.trim();
                                    const isExisting =
                                        inputValue &&
                                        options.some(
                                            (opt) =>
                                                String(
                                                    typeof opt === "string"
                                                        ? opt
                                                        : opt?.title || opt?.inputValue
                                                ).toLowerCase() ===
                                                inputValue.toLowerCase()
                                        );
                                    if (inputValue && !isExisting) {
                                        filtered.push({
                                            inputValue,
                                            title: `Add "${inputValue}"`,
                                        });
                                    }
                                    return filtered;
                                }}
                                renderOption={(props, option) => {
                                    const label =
                                        typeof option === "string" ? option : option.title;
                                    return <li {...props}>{label}</li>;
                                }}
                                renderInput={(params) => (
                                    <TextField required {...params} label={"Territory"} />
                                )}
                            />
                        </Grid>
                        <Grid item xs={2} sx={{ display: "flex", justifyContent: "flex-end" }}>
                            <Tooltip title="Add new territory">
                                <span>
                                    <IconButton
                                        size="small"
                                        onClick={() => {
                                            setNewTerritoryName(
                                                territoryInputValue || ""
                                            );
                                            setNewTerritoryDialogOpen(true);
                                        }}
                                    >
                                        <Add fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item xs={12} sm={12}>
                    <TextField
                        required
                        name="address_line1"
                        label="Address"
                        value={formData.address_line1}
                        onChange={handleChange}
                        fullWidth
                    />
                </Grid>
                <Grid item xs={12} sm={12}>
                    <TextField
                        name="address_line2"
                        label="Address details"
                        value={formData.address_line2}
                        onChange={handleChange}
                        fullWidth
                    />
                </Grid>
                <Grid item xs={12} sm={12}>
                    <TextField
                        name="phone"
                        label="Phone"
                        value={formData.phone}
                        onChange={handleChange}
                        fullWidth
                    />
                </Grid>
            </Grid>
            <div className={classes.ActionsFooter}>
                <Button disabled={loading} onClick={() => handleReset()}>
                    Reset
                </Button>
                <Button
                    disabled={loading}
                    variant="contained"
                    onClick={() => handleSubmit()}
                >
                    {loading && (
                        <CircularProgress
                            size={17}
                            style={{ color: "white", marginRight: "5px" }}
                        />
                    )}
                    Submit
                </Button>
            </div>

            <Dialog
                open={newTerritoryDialogOpen}
                onClose={() => setNewTerritoryDialogOpen(false)}
                fullWidth
                maxWidth="xs"
            >
                <div style={{ padding: 16 }}>
                    <TextField
                        autoFocus
                        label="New territory"
                        value={newTerritoryName}
                        onChange={(e) => setNewTerritoryName(e.target.value)}
                        fullWidth
                    />
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "flex-end",
                            gap: 8,
                            marginTop: 12,
                        }}
                    >
                        <Button
                            onClick={() => setNewTerritoryDialogOpen(false)}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            onClick={() => {
                                createTerritory(newTerritoryName);
                                setNewTerritoryDialogOpen(false);
                            }}
                        >
                            Create
                        </Button>
                    </div>
                </div>
            </Dialog>
        </div>
    );
};

export default NewAddress;
